import { Box, Grid } from '@mui/material';
import TableCard from 'lib/designSystem/Card/TableCard';
import CentralPieChart from 'lib/designSystem/Charts/CentralPieChart';
import EventTimeline, {
  EventTimelineItem,
} from 'lib/designSystem/Charts/EventTimeline';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import SubtitleWithTooltip from 'lib/designSystem/SubtitleWithTooltip';
import CustomToggleButtonGroup from 'lib/designSystem/ToggleButtonGroup';
import { CHART_COLORS } from 'lib/functions/const/colors';
import { formatMoney } from 'lib/functions/utils';
import React, { useMemo, useState } from 'react';

import { useFundSummaryData } from '../../context/FundSummaryDataContext';

type DistributionSeries =
  | 'nomineesFund'
  | 'nomineesBusiness'
  | 'paymentsAmount';

const TOGGLE_DISTRIBUTION_OPTIONS = [
  {
    value: 'nomineesFund',
    label: 'Nóminas por fondo',
    ariaLabel: 'nomineesFund',
  },
  {
    value: 'nomineesBusiness',
    label: 'Nóminas por empresa',
    ariaLabel: 'nomineesBusiness',
  },
  {
    value: 'paymentsAmount',
    label: 'Recaudado por fondo',
    ariaLabel: 'paymentsAmount',
  },
];

const TOGGLE_OVERDUE_OPTIONS = [
  { value: 'dueSoon', label: 'Por vencer', ariaLabel: 'dueSoon' },
  { value: 'overdue', label: 'Mora', ariaLabel: 'overdue' },
];

type OverdueType = 'overdue' | 'dueSoon';

const NomineesAnalytics: React.FC = () => {
  const [distributionSeries, setDistributionSeries] =
    useState<DistributionSeries>('nomineesFund');
  const [overdueType, setOverdueType] = useState<OverdueType>('dueSoon');
  const {
    nomineesAnalytics: data,
    overdueAndDueDates: overdueData,
    paymentsConsolidated: paymentsData,
    isPendingNomineesAnalytics: isPending,
    isErrorNomineesAnalytics: isError,
    isPendingOverdueAndDueDates: isPendingOverdue,
    isErrorOverdueAndDueDates: isErrorOverdue,
    isPendingPaymentsConsolidated: isPendingPayments,
    isErrorPaymentsConsolidated: isErrorPayments,
  } = useFundSummaryData();

  const nomineesByFundPieData = useMemo(() => {
    if (!data?.nomineesByFund?.length) return [];
    return data.nomineesByFund.map((fund) => ({
      name: fund.fundName,
      value: fund.amount,
    }));
  }, [data?.nomineesByFund]);

  const nomineesByBusinessPieData = useMemo(() => {
    if (!data?.nomineesByBusiness?.length) return [];
    return data.nomineesByBusiness.map((business) => ({
      name: business.businessName,
      value: business.amount,
    }));
  }, [data?.nomineesByBusiness]);

  const paymentsByFundAmountPieData = useMemo(() => {
    const byFund = paymentsData?.byFund;
    if (!byFund?.length) return [];
    return byFund.map((fund) => ({
      name: fund.fundName,
      value: fund.amount,
    }));
  }, [paymentsData?.byFund]);

  const currentPieData = useMemo(() => {
    switch (distributionSeries) {
      case 'nomineesFund':
        return nomineesByFundPieData;
      case 'nomineesBusiness':
        return nomineesByBusinessPieData;
      case 'paymentsAmount':
        return paymentsByFundAmountPieData;
      default:
        return nomineesByFundPieData;
    }
  }, [
    distributionSeries,
    nomineesByFundPieData,
    nomineesByBusinessPieData,
    paymentsByFundAmountPieData,
  ]);

  const isDistributionPayments = distributionSeries === 'paymentsAmount';
  const isPendingDistribution = isDistributionPayments
    ? isPendingPayments
    : isPending;
  const isErrorDistribution = isDistributionPayments
    ? isErrorPayments
    : isError;

  const emptyMessage = useMemo(() => {
    switch (distributionSeries) {
      case 'nomineesFund':
        return 'No hay datos de nóminas por fondo en el período.';
      case 'nomineesBusiness':
        return 'No hay datos de nóminas por negocio en el período.';
      case 'paymentsAmount':
        return 'No hay datos de pagos por fondo en el período.';
      default:
        return 'No hay datos disponibles.';
    }
  }, [distributionSeries]);

  const dueSoonTimelineEvents = useMemo((): EventTimelineItem[] => {
    const dueSoonByDays = overdueData?.dueSoonByDays;
    if (!dueSoonByDays?.length) return [];
    return dueSoonByDays.map((item, index) => ({
      key: index,
      label: item.range,
      subtitle: formatMoney(item.amount),
      valueLabel:
        item.count > 1 ? `${item.count} documentos` : `${item.count} documento`,
      variant: item.count > 0 ? 'error' : 'success',
    }));
  }, [overdueData?.dueSoonByDays]);

  const overdueTimelineEvents = useMemo((): EventTimelineItem[] => {
    const overdueByDays = overdueData?.overdueByDays;
    if (!overdueByDays?.length) return [];
    return overdueByDays.map((item, index) => ({
      key: index,
      label: item.range,
      subtitle: formatMoney(item.amount),
      valueLabel:
        item.count > 1 ? `${item.count} documentos` : `${item.count} documento`,
      variant:
        item.count > 0 ? (item.amount > 0 ? 'error' : 'warning') : 'success',
    }));
  }, [overdueData?.overdueByDays]);

  const currentTimelineEvents =
    overdueType === 'overdue' ? overdueTimelineEvents : dueSoonTimelineEvents;
  const emptyTimelineMessage =
    overdueType === 'overdue'
      ? 'No hay documentos vencidos en el período.'
      : 'No hay documentos por vencer en el período.';

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={7}>
          <TableCard sx={{ minHeight: 530 }}>
            <SubtitleWithTooltip
              title="Distribución de montos"
              tooltipContent="Nóminas activas por fondo/empresa o pagos por fondo en el período."
              tooltipPlacement="right"
              variant="compact"
            >
              <CustomToggleButtonGroup
                value={distributionSeries}
                onChange={(value) =>
                  setDistributionSeries(value as DistributionSeries)
                }
                options={TOGGLE_DISTRIBUTION_OPTIONS}
              />
            </SubtitleWithTooltip>
            {isPendingDistribution && (
              <Box
                sx={{
                  minHeight: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CenteredCircularProgress relative />
              </Box>
            )}
            {!isPendingDistribution && isErrorDistribution && (
              <Box sx={{ py: 2, color: 'error.main' }}>
                {isDistributionPayments
                  ? 'Error al cargar datos de pagos por fondo.'
                  : 'Error al cargar análisis de nóminas.'}
              </Box>
            )}
            {!isPendingDistribution &&
              !isErrorDistribution &&
              (currentPieData.length === 0 ? (
                <Box sx={{ py: 2, color: 'text.secondary' }}>
                  {emptyMessage}
                </Box>
              ) : (
                <CentralPieChart
                  key={`distribution-${distributionSeries}`}
                  data={currentPieData}
                  colors={Object.values(CHART_COLORS)}
                  height={300}
                  showLegend={true}
                  yAxisFormat="M"
                />
              ))}
          </TableCard>
        </Grid>
        <Grid item xs={5}>
          <TableCard sx={{ minHeight: 530 }}>
            <SubtitleWithTooltip
              title={
                overdueType === 'overdue' ? 'Mora docs. vencidos' : 'Por vencer'
              }
              tooltipContent={
                overdueType === 'overdue'
                  ? 'Documentos que ya vencieron, agrupados por días de atraso. El monto es la mora restante en $. Esto se calcula sobre las nóminas activas en el período seleccionado.'
                  : 'Documentos que vencen en los próximos 30 días, agrupados por días restantes. El monto es el valor del documento en $. Esto se calcula sobre las nóminas activas en el período seleccionado.'
              }
              tooltipPlacement="right"
              variant="compact"
            >
              <CustomToggleButtonGroup
                value={overdueType}
                onChange={(value) => setOverdueType(value as OverdueType)}
                options={TOGGLE_OVERDUE_OPTIONS}
              />
            </SubtitleWithTooltip>
            {(isPending || isPendingOverdue) && (
              <Box
                sx={{
                  minHeight: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CenteredCircularProgress relative />
              </Box>
            )}
            {!isPending && !isPendingOverdue && (isError || isErrorOverdue) && (
              <Box sx={{ py: 2, color: 'error.main' }}>
                {overdueType === 'overdue'
                  ? 'Error al cargar documentos vencidos en nóminas.'
                  : 'Error al cargar documentos por vencer en nóminas.'}
              </Box>
            )}
            {!isPending &&
              !isPendingOverdue &&
              !isError &&
              !isErrorOverdue &&
              (currentTimelineEvents.length === 0 ? (
                <Box sx={{ py: 2, color: 'text.secondary' }}>
                  {emptyTimelineMessage}
                </Box>
              ) : (
                <Box sx={{ marginLeft: 0, paddingLeft: 0 }}>
                  <EventTimeline
                    events={currentTimelineEvents}
                    position="right"
                  />
                </Box>
              ))}
          </TableCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NomineesAnalytics;
